export const DEFAULT_CHAMPIONSHIP_IMAGE =
  '/img/default/championship-header.webp'
export const DEFAULT_STANDALONE_EVENT_IMAGE =
  '/img/default/standalone-header.webp'

const LOCAL_PUBLIC_IMAGE_FALLBACKS = new Set([
  DEFAULT_CHAMPIONSHIP_IMAGE,
  DEFAULT_STANDALONE_EVENT_IMAGE,
])

type ImageValue = string | null | undefined
type ImageNormalizer = (value: string) => string | null

export type ChampionshipImageSource = Readonly<{
  headerImgUrl?: ImageValue
  isSingleEvent?: boolean | null
  community?: Readonly<{
    headerImgUrl?: ImageValue
  }> | null
}>

export type EventImageSource = Readonly<{
  customTrackImgUrl?: ImageValue
  track?: Readonly<{
    coverImgUrl?: ImageValue
  }> | null
  championship?: ChampionshipImageSource | null
}>

function identityImageNormalizer(value: string): string | null {
  const normalized = value.trim()
  return normalized || null
}

function resolveImage(
  candidates: readonly ImageValue[],
  normalize: ImageNormalizer,
): string | null {
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue
    const normalized = normalize(candidate)
    if (normalized) return normalized
  }
  return null
}

function defaultRacingImage(isSingleEvent?: boolean | null): string {
  return isSingleEvent
    ? DEFAULT_STANDALONE_EVENT_IMAGE
    : DEFAULT_CHAMPIONSHIP_IMAGE
}

export function resolveEventImageUrl(
  event: EventImageSource,
  normalize: ImageNormalizer = identityImageNormalizer,
): string | null {
  return resolveImage(
    [
      event.customTrackImgUrl,
      event.track?.coverImgUrl,
      event.championship?.headerImgUrl,
      event.championship?.community?.headerImgUrl,
      defaultRacingImage(event.championship?.isSingleEvent),
    ],
    normalize,
  )
}

export function resolveChampionshipImageUrl(
  championship: ChampionshipImageSource,
  normalize: ImageNormalizer = identityImageNormalizer,
): string | null {
  return resolveImage(
    [
      championship.headerImgUrl,
      championship.community?.headerImgUrl,
      defaultRacingImage(championship.isSingleEvent),
    ],
    normalize,
  )
}

function publicImageNormalizer(baseUrl?: string): ImageNormalizer {
  let publicBaseUrl = new URL('https://gridfinder.com')

  if (baseUrl) {
    try {
      const parsed = new URL(baseUrl)
      if (parsed.protocol === 'https:') publicBaseUrl = parsed
    } catch {
      // Static fallbacks remain on the public Grid Finder origin.
    }
  }

  return (value) => {
    const normalized = value.trim()
    if (!normalized) return null

    try {
      if (LOCAL_PUBLIC_IMAGE_FALLBACKS.has(normalized)) {
        return new URL(normalized, publicBaseUrl).toString()
      }

      const parsed = new URL(normalized)
      return parsed.protocol === 'https:' ? parsed.toString() : null
    } catch {
      return null
    }
  }
}

export function resolvePublicEventImageUrl(
  event: EventImageSource,
  baseUrl?: string,
): string | null {
  return resolveEventImageUrl(event, publicImageNormalizer(baseUrl))
}

export function resolvePublicChampionshipImageUrl(
  championship: ChampionshipImageSource,
  baseUrl?: string,
): string | null {
  return resolveChampionshipImageUrl(
    championship,
    publicImageNormalizer(baseUrl),
  )
}
